import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getEventById, saveEvent } from "../api/events";
import { WorkflowSteps } from "../components/WorkflowSteps";
import {
  getActiveInviteThemes,
  getInviteRevealById,
  getInviteThemeById,
  getThemeFit,
  INVITE_REVEALS,
  normalizeEventTypeName,
} from "../data/inviteThemes";
import "./EventThemePage.css";

const FEATURE_LABELS = {
  video_hero: "Video",
  rsvp: "RSVP",
  comments: "Comments",
  gallery: "Gallery",
  guest_media: "Guest Media",
  subevents: "Subevents",
  schedule: "Schedule",
  venue: "Venue",
  minimal: "Minimal",
};

const formatDate = (value) => {
  if (!value) return "Date to be announced";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getEventTypeName = (event) =>
  event?.event_type_name || event?.event_type || event?.template?.event_type || "";

const getFitLabel = (fit) => {
  if (fit === "recommended") return "Best match";
  if (fit === "compatible") return "Good fit";
  if (fit === "cross_event") return "Use anyway";
  return "Available";
};

const getEventMetadataValue = (event, key, fallback = "") =>
  (event?.eventmetadata || []).find((meta) => meta.meta_key === key)?.meta_value ||
  event?.[key] ||
  fallback;

const upsertEventMetadata = (metadata = [], key, value) => {
  const next = metadata
    .filter((meta) => meta?.meta_key && meta.meta_key !== key)
    .map((meta) => ({ meta_key: meta.meta_key, meta_value: meta.meta_value }));

  if (value) next.push({ meta_key: key, meta_value: value });
  return next;
};

const getDraftSelectionKey = (eventId) => `event-theme-selection:${eventId}`;

const readDraftSelection = (eventId) => {
  if (!eventId || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(getDraftSelectionKey(eventId));
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
};

const writeDraftSelection = (eventId, selection) => {
  if (!eventId || typeof window === "undefined") return;
  window.sessionStorage.setItem(getDraftSelectionKey(eventId), JSON.stringify(selection));
};

const clearDraftSelection = (eventId) => {
  if (!eventId || typeof window === "undefined") return;
  window.sessionStorage.removeItem(getDraftSelectionKey(eventId));
};

const PreviewPhone = ({ theme, reveal, event }) => {
  const tokens = theme.tokens || {};
  const accent = tokens.accent || "#ff0099";
  const accentAlt = tokens.accentAlt || "#7f2556";
  const surface = tokens.surface || "#ffffff";
  const text = tokens.text || "#2f2030";
  const muted = tokens.muted || "#7b6272";
  const frameStyle = tokens.frameStyle || "clean";
  const eventTitle = event?.title || event?.event_title || "Your Invitation";
  const eventVenue = event?.location || "Venue to be announced";

  return (
    <div
      className="relative overflow-hidden rounded-[38px] border p-5 shadow-[0_34px_90px_rgba(74,24,48,0.18)]"
      style={{
        background: `linear-gradient(150deg, ${tokens.background || "#fff7fb"}, ${
          tokens.backgroundAlt || surface
        })`,
        borderColor: tokens.border || "rgba(242, 220, 231, 0.9)",
      }}
    >
      <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-35" style={{ background: accent }} />
      <div className="absolute -bottom-28 left-4 h-80 w-80 rounded-full opacity-25" style={{ background: surface }} />
      <div className="relative z-10 mx-auto w-full max-w-[430px] overflow-hidden rounded-[34px] border bg-white/55 p-3">
        <div className="relative aspect-[9/16] overflow-hidden rounded-[28px] shadow-2xl" style={{ background: surface }}>
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(circle at 50% 20%, ${accent}55, transparent 30%), linear-gradient(180deg, ${surface} 0%, ${
                tokens.surfaceSoft || surface
              } 100%)`,
            }}
          />

          {reveal.id === "curtain-open" ? (
            <>
              <div
                className={`absolute inset-y-0 left-0 z-30 w-1/2 ${
                  frameStyle === "clean" ? "bg-slate-900/85" : "bg-[#4a1022]/90"
                } animate-[themeCurtainLeft_4s_ease-in-out_infinite]`}
              />
              <div
                className={`absolute inset-y-0 right-0 z-30 w-1/2 ${
                  frameStyle === "clean" ? "bg-slate-900/85" : "bg-[#4a1022]/90"
                } animate-[themeCurtainRight_4s_ease-in-out_infinite]`}
              />
            </>
          ) : null}

          {reveal.id === "soft-fade" ? (
            <div className="absolute inset-0 z-30 animate-[themeSoftFade_4s_ease-in-out_infinite] bg-white" />
          ) : null}

          {reveal.id === "golden-glow" ? (
            <div
              className="absolute inset-0 z-30 animate-[themeGoldenGlow_4s_ease-in-out_infinite]"
              style={{ background: `radial-gradient(circle at center, ${accent}cc, transparent 38%)` }}
            />
          ) : null}

          {reveal.id === "confetti-pop" ? (
            <div className="absolute inset-0 z-30 animate-[themeConfettiPop_4s_ease-in-out_infinite]">
              {[...Array(16)].map((_, index) => (
                <span
                  key={index}
                  className="absolute h-2.5 w-2.5 rounded-full"
                  style={{
                    left: `${9 + ((index * 17) % 82)}%`,
                    top: `${14 + ((index * 23) % 72)}%`,
                    background: index % 2 ? accent : accentAlt,
                  }}
                />
              ))}
            </div>
          ) : null}
          {reveal.id === "spotlight-rise" ? (
            <div className="absolute inset-0 z-30 animate-[themeSpotlightRise_4s_ease-in-out_infinite] bg-[radial-gradient(ellipse_at_center,transparent_18%,rgba(0,0,0,0.72)_58%)]" />
          ) : null}
          {reveal.id === "floral-bloom" ? (
            <div className="absolute inset-0 z-30 animate-[themeFloralBloom_4s_ease-in-out_infinite]">
              <span className="absolute -left-6 -top-6 h-24 w-24 rounded-full border-[18px] border-[#f3bddc]" />
              <span className="absolute -right-6 bottom-16 h-24 w-24 rounded-full border-[18px] border-[#f2b705]" />
              <span className="absolute bottom-4 left-14 h-16 w-16 rounded-full border-[12px] border-[#ff0099]" />
            </div>
          ) : null}
          {reveal.id === "slide-up-card" ? (
            <div
              className="absolute inset-x-7 bottom-7 z-30 h-44 animate-[themeSlideUpCard_4s_ease-in-out_infinite] rounded-3xl border shadow-2xl"
              style={{ background: surface, borderColor: tokens.border || "rgba(0,0,0,0.08)" }}
            />
          ) : null}
          {reveal.id === "sparkle-frame" ? (
            <div className="absolute inset-5 z-30 animate-[themeSparkleFrame_4s_ease-in-out_infinite] rounded-[26px] border-4 border-[#f2b705] shadow-[0_0_32px_rgba(242,183,5,0.55)]">
              <span className="absolute -right-2 top-8 h-3 w-3 rounded-full bg-white" />
              <span className="absolute bottom-12 left-2 h-2 w-2 rounded-full bg-white" />
              <span className="absolute right-10 top-2 h-2 w-2 rounded-full bg-white" />
            </div>
          ) : null}
          {reveal.id === "balloon-float" ? (
            <div className="absolute inset-0 z-30 animate-[themeBalloonFloat_4s_ease-in-out_infinite]">
              {[...Array(9)].map((_, index) => (
                <span
                  key={index}
                  className="absolute h-12 w-9 rounded-full shadow-lg"
                  style={{
                    left: `${10 + ((index * 11) % 78)}%`,
                    bottom: `${-8 + (index % 3) * 8}%`,
                    background: ["#ff6b6b", "#ffd166", "#4dabf7", "#ff0099", "#8ce99a"][index % 5],
                    animationDelay: `${index * 70}ms`,
                  }}
                >
                  <span className="absolute left-1/2 top-full h-8 w-px -translate-x-1/2 bg-white/70" />
                </span>
              ))}
            </div>
          ) : null}
          {reveal.id === "petal-blessing" ? (
            <div className="absolute inset-0 z-30">
              {[...Array(28)].map((_, index) => (
                <span
                  key={index}
                  className="absolute grid place-items-center text-xl leading-none animate-[themePetalFall_4.2s_ease-in-out_infinite]"
                  style={{
                    left: `${5 + ((index * 13) % 90)}%`,
                    top: `${-22 - (index % 5) * 6}%`,
                    color: index % 3 === 0 ? "#ff8fab" : index % 2 ? "#ffd166" : accent,
                    animationDelay: `${index * 65}ms`,
                    fontSize: `${17 + (index % 4) * 3}px`,
                    "--petal-drift": `${((index % 7) - 3) * 10}px`,
                    "--petal-rotate": `${120 + index * 17}deg`,
                  }}
                >
                  {["✿", "✽", "✼", "❀"][index % 4]}
                </span>
              ))}
            </div>
          ) : null}

          <div className="absolute inset-x-8 top-10 text-center">
            <p className="text-[10px] font-black uppercase tracking-[0.24em]" style={{ color: accentAlt }}>
              {reveal.name}
            </p>
            <h3
              className="mt-3 text-2xl font-black leading-tight"
              style={{ color: text, fontFamily: tokens.headingFont }}
            >
              {eventTitle}
            </h3>
          </div>

          <div className="absolute inset-x-7 top-[31%] overflow-hidden rounded-3xl shadow-xl">
            <div
              className="aspect-video"
              style={{
                background: `linear-gradient(135deg, ${surface}, ${tokens.surfaceSoft || surface})`,
                border: `1px solid ${tokens.border || "rgba(0,0,0,0.08)"}`,
              }}
            >
              <div className="flex h-full items-center justify-center">
                <span className="rounded-full bg-white/70 px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em]" style={{ color: accentAlt }}>
                  Invitation Video
                </span>
              </div>
            </div>
          </div>

          <div
            className="absolute inset-x-7 bottom-7 rounded-3xl border p-4"
            style={{ background: surface, borderColor: tokens.border || "rgba(0,0,0,0.08)", color: text }}
          >
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: accentAlt }}>
                RSVP & Details
              </span>
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: accent }} />
            </div>
            <p className="text-sm font-bold leading-6" style={{ color: text }}>
              {formatDate(event?.event_date || event?.date)}
            </p>
            <p className="text-xs leading-5" style={{ color: muted }}>
              {eventVenue}
            </p>
            <div className="mt-3 w-fit rounded-full px-4 py-2 text-xs font-black" style={{ background: accent, color: surface }}>
              RSVP Now
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ThemeOption = ({ theme, fit, active, saved, onSelect }) => {
  const tokens = theme.tokens || {};

  return (
    <button
      type="button"
      onClick={() => onSelect(theme.id)}
      className={`rounded-2xl border p-3 text-left transition hover:border-[#ff0099] ${
        active ? "border-[#ff0099] bg-[#fff1f8]" : "border-[#f1dde7] bg-white"
      }`}
    >
      <div className="flex gap-3">
        <span
          className="relative mt-1 h-16 w-16 shrink-0 overflow-hidden rounded-2xl border"
          style={{
            background: `linear-gradient(135deg, ${tokens.background || "#fff"}, ${tokens.accent || "#ff0099"})`,
            borderColor: tokens.border || "#f1dde7",
          }}
        >
          <span
            className="absolute inset-x-2 top-2 h-5 rounded-lg"
            style={{ background: tokens.surface || "#fff" }}
          />
          <span
            className="absolute inset-x-2 bottom-2 h-7 rounded-xl"
            style={{ background: `linear-gradient(135deg, ${tokens.accentAlt || tokens.background}, ${tokens.accent})` }}
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-start justify-between gap-2">
            <strong className="block text-sm font-black leading-tight text-[#2f2030]">{theme.name}</strong>
            {saved ? <em className="shrink-0 rounded-full bg-[#201726] px-2 py-1 text-[9px] not-italic text-white">Saved</em> : null}
          </span>
          <span className="mt-1 block text-xs leading-5 text-[#7b6272]">{theme.description}</span>
          <span className="mt-2 flex flex-wrap gap-1.5">
            <em className="rounded-full bg-[#f8f1f5] px-2 py-1 text-[10px] not-italic font-black text-[#9d557d]">
              {getFitLabel(fit)}
            </em>
            {(theme.featureTags || []).slice(0, 2).map((tag) => (
              <em key={tag} className="rounded-full bg-[#f8f1f5] px-2 py-1 text-[10px] not-italic font-bold text-[#806879]">
                {FEATURE_LABELS[tag] || tag}
              </em>
            ))}
          </span>
        </span>
      </div>
    </button>
  );
};

const RevealThumbnail = ({ revealId, active }) => (
  <span
    className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-2xl border ${
      active ? "border-[#ff0099]" : "border-[#f1dde7]"
    } bg-[#fff7fb]`}
  >
    <span className="absolute inset-2 rounded-xl bg-gradient-to-br from-[#fff7ea] to-[#f3bddc]" />
    {revealId === "curtain-open" ? (
      <>
        <span className="absolute inset-y-0 left-0 w-1/2 animate-[themeCurtainLeft_4s_ease-in-out_infinite] bg-[#4a1022]" />
        <span className="absolute inset-y-0 right-0 w-1/2 animate-[themeCurtainRight_4s_ease-in-out_infinite] bg-[#4a1022]" />
      </>
    ) : null}
    {revealId === "soft-fade" ? (
      <span className="absolute inset-0 animate-[themeSoftFade_4s_ease-in-out_infinite] bg-white" />
    ) : null}
    {revealId === "golden-glow" ? (
      <span className="absolute inset-0 animate-[themeGoldenGlow_4s_ease-in-out_infinite] bg-[radial-gradient(circle_at_center,#e7a927,transparent_42%)]" />
    ) : null}
    {revealId === "confetti-pop" ? (
      <span className="absolute inset-0 animate-[themeConfettiPop_4s_ease-in-out_infinite]">
        {[...Array(7)].map((_, index) => (
          <span
            key={index}
            className="absolute h-1.5 w-1.5 rounded-full bg-[#ff0099]"
            style={{
              left: `${18 + ((index * 19) % 64)}%`,
              top: `${18 + ((index * 23) % 64)}%`,
              background: index % 2 ? "#ff0099" : "#f2b705",
            }}
          />
        ))}
      </span>
    ) : null}
    {revealId === "spotlight-rise" ? (
      <span className="absolute inset-0 animate-[themeSpotlightRise_4s_ease-in-out_infinite] bg-[radial-gradient(ellipse_at_center,transparent_18%,rgba(0,0,0,0.7)_58%)]" />
    ) : null}
    {revealId === "floral-bloom" ? (
      <span className="absolute inset-0 animate-[themeFloralBloom_4s_ease-in-out_infinite]">
        <span className="absolute -left-3 -top-3 h-9 w-9 rounded-full border-[8px] border-[#f3bddc]" />
        <span className="absolute -right-2 bottom-5 h-10 w-10 rounded-full border-[8px] border-[#f2b705]" />
      </span>
    ) : null}
    {revealId === "slide-up-card" ? (
      <span className="absolute inset-x-3 bottom-2 h-8 animate-[themeSlideUpCard_4s_ease-in-out_infinite] rounded-xl bg-white shadow-lg" />
    ) : null}
    {revealId === "sparkle-frame" ? (
      <span className="absolute inset-2 animate-[themeSparkleFrame_4s_ease-in-out_infinite] rounded-xl border-2 border-[#f2b705]" />
    ) : null}
    {revealId === "balloon-float" ? (
      <span className="absolute inset-0 animate-[themeBalloonFloat_4s_ease-in-out_infinite]">
        {[...Array(5)].map((_, index) => (
          <span
            key={index}
            className="absolute h-4 w-3 rounded-full"
            style={{
              left: `${12 + index * 17}%`,
              bottom: `${2 + (index % 2) * 10}%`,
              background: ["#ff6b6b", "#ffd166", "#4dabf7", "#ff0099", "#8ce99a"][index % 5],
            }}
          />
        ))}
      </span>
    ) : null}
    {revealId === "petal-blessing" ? (
      <span className="absolute inset-0">
        {[...Array(14)].map((_, index) => (
          <span
            key={index}
            className="absolute grid place-items-center text-sm leading-none animate-[themePetalFall_4.2s_ease-in-out_infinite]"
            style={{
              left: `${8 + ((index * 13) % 80)}%`,
              top: `${-24 - (index % 4) * 8}%`,
              color: index % 3 === 0 ? "#ff8fab" : index % 2 ? "#ffd166" : "#f28c18",
              animationDelay: `${index * 75}ms`,
              fontSize: `${11 + (index % 3) * 2}px`,
              "--petal-drift": `${((index % 5) - 2) * 5}px`,
              "--petal-rotate": `${120 + index * 21}deg`,
            }}
          >
            {["✿", "✽", "✼", "❀"][index % 4]}
          </span>
        ))}
      </span>
    ) : null}
    <span className="absolute inset-x-4 bottom-3 h-2 rounded-full bg-black/20" />
  </span>
);

export const EventThemePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const eventId = location.state?.eventId;

  const [event, setEvent] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState("classic-professional");
  const [savedTheme, setSavedTheme] = useState("classic-professional");
  const [selectedReveal, setSelectedReveal] = useState("curtain-open");
  const [savedReveal, setSavedReveal] = useState("curtain-open");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeMood, setActiveMood] = useState("all");
  const [activeFeature, setActiveFeature] = useState("all");
  const [activeControlTab, setActiveControlTab] = useState("themes");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) return;
      try {
        const data = await getEventById(eventId);
        const initialTheme = data.theme_id || "classic-professional";
        const initialReveal = getEventMetadataValue(data, "reveal_id", "curtain-open");
        const draftSelection = readDraftSelection(data.id || eventId);
        const draftTheme = draftSelection?.themeId || initialTheme;
        const draftReveal = draftSelection?.revealId || initialReveal;
        setEvent(data);
        setSelectedTheme(draftTheme);
        setSavedTheme(initialTheme);
        setSelectedReveal(draftReveal);
        setSavedReveal(initialReveal);
      } catch (error) {
        console.error("Error fetching event for theme selection:", error);
      }
    };

    fetchEvent();
  }, [eventId]);

  const eventTypeName = getEventTypeName(event);
  const normalizedEventType = normalizeEventTypeName(eventTypeName);
  const themes = useMemo(() => getActiveInviteThemes(), []);
  const selectedThemeDetails = getInviteThemeById(selectedTheme);
  const selectedRevealDetails = getInviteRevealById(selectedReveal);
  const hasUnsavedChange = selectedTheme !== savedTheme || selectedReveal !== savedReveal;

  const moodFilters = useMemo(() => {
    const values = new Set();
    themes.forEach((theme) => (theme.moodTags || []).forEach((tag) => values.add(tag)));
    return ["all", ...Array.from(values).sort()];
  }, [themes]);

  const featureFilters = useMemo(() => {
    const values = new Set();
    themes.forEach((theme) => (theme.featureTags || []).forEach((tag) => values.add(tag)));
    return ["all", ...Array.from(values).sort()];
  }, [themes]);

  const filteredThemes = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return themes
      .filter((theme) => {
        if (activeMood !== "all" && !(theme.moodTags || []).includes(activeMood)) return false;
        if (activeFeature !== "all" && !(theme.featureTags || []).includes(activeFeature)) return false;
        if (!query) return true;
        return [
          theme.name,
          theme.description,
          ...(theme.styleTags || []),
          ...(theme.toneTags || []),
          ...(theme.moodTags || []),
          ...(theme.featureTags || []),
        ]
          .join(" ")
          .toLowerCase()
          .includes(query);
      })
      .sort((a, b) => {
        const rank = { recommended: 0, compatible: 1, all: 2, cross_event: 3, restricted: 4 };
        return (
          (rank[getThemeFit(a, normalizedEventType)] ?? 2) -
            (rank[getThemeFit(b, normalizedEventType)] ?? 2) || (a.sortOrder || 0) - (b.sortOrder || 0)
        );
      });
  }, [activeFeature, activeMood, normalizedEventType, searchTerm, themes]);

  const selectedFit = getThemeFit(selectedThemeDetails, normalizedEventType);
  const unusualChoice = selectedFit === "cross_event";

  useEffect(() => {
    if (!event?.id) return;
    writeDraftSelection(event.id, {
      themeId: selectedTheme,
      revealId: selectedReveal,
    });
  }, [event?.id, selectedReveal, selectedTheme]);

  const handleFullPreview = () => {
    writeDraftSelection(event.id, {
      themeId: selectedTheme,
      revealId: selectedReveal,
    });
    navigate(`/events/${event.id}/experience`, {
      state: {
        themeOverride: selectedTheme,
        revealOverride: selectedReveal,
        returnTo: "/event-theme",
        returnState: { eventId: event.id },
      },
    });
  };

  const handleSaveTheme = async () => {
    if (!event) return;

    try {
      setIsSaving(true);
      setSaveError("");

      const payload = {
        ...event,
        theme_id: selectedTheme,
        reveal_id: selectedReveal,
        eventmetadata: upsertEventMetadata(event.eventmetadata || [], "reveal_id", selectedReveal),
      };

      delete payload.owner_id;
      delete payload.created_date;
      await saveEvent({ mainEvent: payload, eventId: event.id });
      const persistedEvent = await getEventById(event.id);
      const persistedTheme = persistedEvent.theme_id || selectedTheme;
      const persistedReveal = getEventMetadataValue(persistedEvent, "reveal_id", selectedReveal);
      setEvent(persistedEvent);
      setSelectedTheme(persistedTheme);
      setSelectedReveal(persistedReveal);
      setSavedTheme(persistedTheme);
      setSavedReveal(persistedReveal);
      clearDraftSelection(event.id);
      navigate("/guest-manager", { state: { eventId: event.id } });
    } catch (error) {
      console.error("Error saving theme:", error);
      setSaveError(error?.message || "Unable to save theme right now.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!event) return <p>Loading theme options...</p>;

  return (
    <div className="min-h-screen bg-[#fff7fb] font-sans">
      <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-6 p-4 md:p-8 xl:p-10">
        <WorkflowSteps currentStep="theme" />

        <main className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_430px]">
          <section className="flex flex-col gap-5 rounded-[34px] border border-[#f2dce7] bg-white p-5 shadow-[0_24px_70px_rgba(125,70,105,0.08)]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#b07c98]">
                  Live Look
                </p>
                <h2 className="mt-2 text-3xl font-black text-[#2f2030]">{selectedThemeDetails.name}</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-[#7b6272]">
                  {selectedThemeDetails.description} Reveal: {selectedRevealDetails.name}.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-[#fff3f9] px-3 py-2 text-xs font-black text-[#a94f7d]">
                  {getFitLabel(selectedFit)}
                </span>
                {hasUnsavedChange ? (
                  <span className="rounded-full bg-[#fff0c2] px-3 py-2 text-xs font-black text-[#7a4b00]">
                    Unsaved preview
                  </span>
                ) : (
                  <span className="rounded-full bg-[#201726] px-3 py-2 text-xs font-black text-white">
                    Current saved setup
                  </span>
                )}
              </div>
            </div>

            {unusualChoice ? (
              <div className="rounded-2xl border border-[#ffe1a8] bg-[#fff9e8] px-4 py-3 text-sm leading-6 text-[#7a4b00]">
                This theme is not a typical match for {eventTypeName || "this event type"}, but you can still use it.
              </div>
            ) : null}

            <PreviewPhone theme={selectedThemeDetails} reveal={selectedRevealDetails} event={event} />

            <div className="flex flex-col justify-end gap-3 sm:flex-row">
                <button
                  type="button"
                  className="rounded-2xl border border-[#f1d2e5] px-5 py-3 font-bold text-[#7f2556] transition-all hover:border-[#ff0099] hover:text-[#ff0099]"
                  onClick={handleFullPreview}
                >
                  Full Preview
                </button>
                <button
                  type="button"
                  className="rounded-2xl bg-[#ff0099] px-5 py-3 font-bold text-white shadow-lg shadow-[#ff0099]/20 transition-all hover:bg-black disabled:cursor-not-allowed disabled:opacity-70"
                  onClick={handleSaveTheme}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : hasUnsavedChange ? "Use Setup" : "Continue to Guests"}
                </button>
            </div>

            {saveError ? (
              <div className="rounded-2xl border border-[#f5c6d7] bg-[#fff4f8] px-4 py-3 text-sm text-[#9d295d]">
                {saveError}
              </div>
            ) : null}
          </section>

          <aside className="sticky top-6 flex max-h-[calc(100vh-48px)] flex-col gap-4 overflow-hidden rounded-[34px] border border-[#f2dce7] bg-white/95 p-5 shadow-[0_24px_60px_rgba(125,70,105,0.1)]">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#b07c98]">
                Controls
              </p>
              <h2 className="mt-2 text-2xl font-black text-[#2f2030]">Try styles quickly</h2>
            </div>

            <div className="grid grid-cols-2 rounded-[22px] border border-[#f1dde7] bg-[#fffafd] p-1">
              {[
                { id: "themes", label: "Themes", value: selectedThemeDetails.name },
                { id: "reveals", label: "Reveals", value: selectedRevealDetails.name },
              ].map((tab) => {
                const active = activeControlTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveControlTab(tab.id)}
                    className={`rounded-[18px] px-3 py-3 text-left transition ${
                      active ? "bg-[#ff0099] text-white shadow-lg shadow-[#ff0099]/15" : "text-[#6f5969]"
                    }`}
                  >
                    <span className="block text-[10px] font-black uppercase tracking-[0.18em]">
                      {tab.label}
                    </span>
                    <span className="mt-1 block truncate text-xs font-bold opacity-90">{tab.value}</span>
                  </button>
                );
              })}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              {activeControlTab === "themes" ? (
                <section className="grid gap-3">
                  <input
                    value={searchTerm}
                    onChange={(eventObj) => setSearchTerm(eventObj.target.value)}
                    placeholder="Search royal, RSVP, birthday..."
                    className="rounded-2xl border border-[#f1dde7] bg-[#fffafd] px-4 py-3 text-sm text-[#2f2030] outline-none transition focus:border-[#ff0099]"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={activeMood}
                      onChange={(eventObj) => setActiveMood(eventObj.target.value)}
                      className="rounded-2xl border border-[#f1dde7] bg-[#fffafd] px-3 py-3 text-sm font-bold text-[#2f2030] outline-none transition focus:border-[#ff0099]"
                    >
                      {moodFilters.map((tag) => (
                        <option key={tag} value={tag}>
                          {tag === "all" ? "All moods" : tag}
                        </option>
                      ))}
                    </select>
                    <select
                      value={activeFeature}
                      onChange={(eventObj) => setActiveFeature(eventObj.target.value)}
                      className="rounded-2xl border border-[#f1dde7] bg-[#fffafd] px-3 py-3 text-sm font-bold text-[#2f2030] outline-none transition focus:border-[#ff0099]"
                    >
                      {featureFilters.map((tag) => (
                        <option key={tag} value={tag}>
                          {tag === "all" ? "All features" : FEATURE_LABELS[tag] || tag}
                        </option>
                      ))}
                    </select>
                  </div>

                  <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#b07c98]">
                    Themes
                  </p>
                  {filteredThemes.map((themeOption) => (
                    <ThemeOption
                      key={themeOption.id}
                      theme={themeOption}
                      fit={getThemeFit(themeOption, normalizedEventType)}
                      active={selectedTheme === themeOption.id}
                      saved={savedTheme === themeOption.id}
                      onSelect={setSelectedTheme}
                    />
                  ))}
                  {!filteredThemes.length ? (
                    <p className="rounded-2xl bg-[#fffafd] p-4 text-sm text-[#7b6272]">
                      No themes match those filters.
                    </p>
                  ) : null}
                </section>
              ) : (
                <section className="grid gap-3">
                  <div className="rounded-[24px] border border-[#f1dde7] bg-[#fffafd] p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#b07c98]">
                          Reveals
                        </p>
                        <h3 className="mt-2 text-sm font-black text-[#2f2030]">
                          {selectedRevealDetails.name}
                        </h3>
                      </div>
                      {selectedReveal !== savedReveal ? (
                        <span className="rounded-full bg-[#fff0c2] px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.12em] text-[#7a4b00]">
                          Unsaved
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-2 text-xs leading-5 text-[#7b6272]">
                      {selectedRevealDetails.description}
                    </p>
                  </div>

                  {INVITE_REVEALS.map((revealOption) => {
                    const active = selectedReveal === revealOption.id;
                    return (
                      <button
                        key={revealOption.id}
                        type="button"
                        onClick={() => setSelectedReveal(revealOption.id)}
                        className={`rounded-2xl border p-4 text-left transition hover:border-[#ff0099] ${
                          active ? "border-[#ff0099] bg-[#fff1f8]" : "border-[#f1dde7] bg-white"
                        }`}
                      >
                        <span className="flex gap-3">
                          <RevealThumbnail revealId={revealOption.id} active={active} />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-start justify-between gap-2">
                              <strong className="text-sm font-black text-[#2f2030]">
                                {revealOption.name}
                              </strong>
                              {savedReveal === revealOption.id ? (
                                <em className="rounded-full bg-[#201726] px-2 py-1 text-[9px] not-italic text-white">
                                  Saved
                                </em>
                              ) : null}
                            </span>
                            <span className="mt-1 block text-xs leading-5 text-[#7b6272]">
                              {revealOption.description}
                            </span>
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </section>
              )}
            </div>
          </aside>
        </main>
      </div>
    </div>
  );
};
